import { defineStore } from 'pinia'
import type { INotificationSoundSettings } from '../types/notificationSettings.types'

/**
 * Стор настроек звуковых оповещений для уведомлений (frontend-only).
 *
 * Бек настройки звука не отдаёт, поэтому всё хранится в localStorage по ключу
 * `materio.notifications.sound`.
 *
 * При выключенном мастер-свитче (`enabled=false`) `shouldPlay` возвращает false для всех
 * категорий. Категории включают звук для соответствующего цвета уведомления (см.
 * useNotificationType().color: error/warning/info).
 *
 * Режим DnD блокирует звук в указанном окне времени (поддержка перехода через полночь).
 */

const STORAGE_KEY = 'materio.notifications.sound'

const DEFAULT_SETTINGS: INotificationSoundSettings = {
  enabled: true,
  critical: true,
  important: true,
  info: true,
  dnd: {
    enabled: false,
    from: '22:00',
    to: '08:00',
  },
}

function loadFromStorage(): INotificationSoundSettings {
  if (typeof window === 'undefined') return { ...DEFAULT_SETTINGS }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_SETTINGS }
    const parsed = JSON.parse(raw)

    return {
      ...DEFAULT_SETTINGS,
      ...parsed,
      dnd: { ...DEFAULT_SETTINGS.dnd, ...(parsed.dnd ?? {}) },
    }
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

function saveToStorage(settings: INotificationSoundSettings): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    /* приватный режим / переполнение — тихо */
  }
}

/** Парсит «HH:mm» в число минут от начала суток. */
function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  if (isNaN(h) || isNaN(m)) return 0

  return h * 60 + m
}

/** Внутри ли текущее время окна DnD `from`..`to` (поддержка перехода через полночь). */
function isInsideDnd(from: string, to: string): boolean {
  const now = new Date()
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const fromMin = timeToMinutes(from)
  const toMin = timeToMinutes(to)
  if (fromMin === toMin) return false

  if (fromMin < toMin) {
    return nowMin >= fromMin && nowMin < toMin
  }

  return nowMin >= fromMin || nowMin < toMin
}

export const useNotificationSoundStore = defineStore('notificationSoundStore', {
  state: () => loadFromStorage(),

  actions: {
    setEnabled(value: boolean): void {
      this.enabled = value
      saveToStorage(this.$state)
    },

    setCategoryEnabled(category: 'critical' | 'important' | 'info', value: boolean): void {
      this[category] = value
      saveToStorage(this.$state)
    },

    setDndEnabled(value: boolean): void {
      this.dnd.enabled = value
      saveToStorage(this.$state)
    },

    setDndRange(from: string, to: string): void {
      this.dnd.from = from
      this.dnd.to = to
      saveToStorage(this.$state)
    },

    /**
     * Решает, надо ли проигрывать звук для уведомления указанной семантической категории.
     * @param visualColor цвет из getNotificationVisual().color (error|warning|info|primary)
     */
    shouldPlay(visualColor: string | undefined): boolean {
      if (!this.enabled) return false

      if (this.dnd.enabled && isInsideDnd(this.dnd.from, this.dnd.to)) {
        return false
      }

      if (visualColor === 'error') return this.critical
      if (visualColor === 'warning') return this.important
      if (visualColor === 'info') return this.info

      return true
    },
  },
})
